import moment from 'moment'
import { Moment } from 'moment'
import { employeeAPI, suggestionAPI, basePath } from '../api'
import {
  CreateEmployeeRequest,
  EmployeeInfoResponse,
  EmployeeNotLoggedInResponse,
  EmployeeReviewerResponse,
  EmployeeSummaryResponse,
  EmployeeWithTeamsAndReviewsResponse,
  MapScoreResponse,
  ReviewSimpleProjection,
  SuggestionBoxMessageRequest,
  TeamInfoResponse,
  UpdateEmployeeRequest,
} from '../api/openapi'
import {
  EMPLOYEE_HIGHLIGHT_GROWTH_TALENT,
  EMPLOYEE_HIGHLIGHT_ROCKSTAR,
  EMPLOYEE_STATUS_DONE,
  EMPLOYEE_STATUS_SKIPPED,
  EMPLOYEE_STATUS_TODO,
  EMPLOYEE_STATUS_UNFINISHED,
} from '../constants/employee'
import { Period } from '../types/definitions/Period'
import { Team } from '../types/definitions/team'
import { Roles } from '../utils/routes'
import { SortBy } from '../utils/sortUtils'
import { simpleTextCompareFn } from '../utils/text'
import { cloneDeep } from '../utils/utils'
import { isTalentRankTitle } from '../utils/talentRankTitle'
import { PeriodService } from './PeriodService'
import { ReviewService } from './ReviewService'
import { EmployeeOverview } from './TeamService'

export interface TalentRankedDeveloper {
  id: number
  name: string
  jobTitle: string
  email: string
  selectedTalentRank: string
  suggestedTalentRank?: string
  overrideReason?: string
  lastReviewDate: string
  leaders: EmployeeInfoResponse[]
}

export interface PaginatedResponse<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
  first?: boolean
  last?: boolean
  numberOfElements?: number
  empty?: boolean
}

// TODO: Mask the values received from EmployeeSummaryResponse and EmployeeWithTeamsAndReviewsResponse
export interface Employee extends EmployeeSummaryResponse {
  role: Roles
  status?: string
  weight?: number
  highlights?: string[]
  leadersAsString?: string
  leaders?: EmployeeInfoResponse[]
  mapScores?: MapScoreResponse[]
}

export type EmployeeKeys = keyof Employee
export type EmployeeValues = Employee[EmployeeKeys]
export type ReviewSimple = ReviewSimpleProjection
export interface EmployeeReviewer extends EmployeeReviewerResponse {
  status: string
}
export interface EmployeeDetails extends EmployeeWithTeamsAndReviewsResponse {
  role: Roles
}

export interface EmployeeScores extends EmployeeOverview {
  isGlobalScore: boolean
  score: number
  skipped: boolean
  tenX?: boolean
}

export type EmployeeSort = SortBy

const teamsCompareFn = (teams1: Team[], teams2: Team[]) => {
  const teamNames1: string = (teams1 || [])
    .map(({ name }) => name)
    .join('')
    .toLowerCase()
  const teamNames2: string = (teams2 || [])
    .map(({ name }) => name)
    .join('')
    .toLowerCase()
  return simpleTextCompareFn(teamNames1, teamNames2)
}

const statusOrder: Record<string, number> = { Completed: 0, Requested: 1, Skipped: 2, Started: 3, 'Not Started': 4 }
const statusCompareFn = (status1: string, status2: string) => {
  return (statusOrder[status1 || ''] || -1) - (statusOrder[status2 || ''] || -1)
}

const teamLeaderCompareFn = (teams1: TeamInfoResponse[] = [], teams2: TeamInfoResponse[] = []) => {
  const teamLeader1: string = teams1
    .map(team => (team.leaders || []).map(leader => leader.name))
    .join('')
    .toLowerCase()
  const teamLeader2: string = teams2
    .map(team => (team.leaders || []).map(leader => leader.name))
    .join('')
    .toLowerCase()
  return simpleTextCompareFn(teamLeader1, teamLeader2)
}

const leadersCompareFn = (leaders1: string[] = [], leaders2: string[] = []) => {
  const leader1: string = leaders1.join(', ')
  const leader2: string = leaders2.join(', ')
  return simpleTextCompareFn(leader1, leader2)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EmployeeSortFn: Partial<Record<string, (item1: any, item2: any) => number>> = {
  id: (emp1, emp2) => simpleTextCompareFn(emp1.id, emp2.id),
  email: (emp1, emp2) => simpleTextCompareFn(emp1.email, emp2.email),
  name: (emp1, emp2) => simpleTextCompareFn(emp1.name, emp2.name),
  jobTitle: (emp1, emp2) => simpleTextCompareFn(emp1.jobTitle, emp2.jobTitle),
  lastReviewType: (emp1, emp2) => simpleTextCompareFn(emp2.lastReviewType || '', emp1.lastReviewType || ''),
  createdDate: (emp1, emp2) => simpleTextCompareFn(emp1.createdDate, emp2.createdDate),
  salaryGrade: (emp1, emp2) => simpleTextCompareFn(emp1.salaryGrade, emp2.salaryGrade),
  teams: (emp1, emp2) => teamsCompareFn(emp1.teams, emp2.teams),
  status: (emp1, emp2) => statusCompareFn(emp1.status, emp2.status),
  leader: (emp1, emp2) => teamLeaderCompareFn(emp1.teams, emp2.teams),
  leaders: (emp1, emp2) => leadersCompareFn(emp1.leaders, emp2.leaders),
  role: (emp1, emp2) => simpleTextCompareFn(emp1.role, emp2.role),
}

export const checkReviewer = (employee: Employee, currentUser?: Employee): boolean => {
  const leaderInclude = (employee.teams || []).filter(team =>
    (team.leaders || []).some(leader => leader.id === currentUser?.id)
  ).length
  const reviewersInclude = (employee.reviewers || []).filter(
    reviewer => reviewer.reviewer?.id === currentUser?.id
  ).length
  return leaderInclude > 0 || reviewersInclude > 0
}

export const getEmployeeStatus = (employee: Employee, currentUser?: Employee) => {
  const { lastOwnReview, lastReview } = employee
  const hasSkippedByCurrentUser = employee.skippedReviews?.some(review => review.author?.id === currentUser?.id)

  if (
    checkReviewer(employee, currentUser) &&
    !lastOwnReview &&
    lastReview &&
    !lastReview?.finishDate &&
    !hasSkippedByCurrentUser
  )
    return EMPLOYEE_STATUS_UNFINISHED

  if (checkReviewer(employee, currentUser) && !lastOwnReview && !lastReview?.finishDate && !hasSkippedByCurrentUser)
    return EMPLOYEE_STATUS_TODO

  if (lastOwnReview && !lastOwnReview?.finishDate) return EMPLOYEE_STATUS_UNFINISHED

  if (lastOwnReview?.finishDate || (checkReviewer(employee, currentUser) && lastReview?.finishDate))
    return EMPLOYEE_STATUS_DONE

  if (hasSkippedByCurrentUser || employee.skippedReviews?.length) return EMPLOYEE_STATUS_SKIPPED

  return EMPLOYEE_STATUS_TODO
}

const normalize = (value?: string | null) => value?.toLowerCase().replace(/[^a-z0-9]/g, '') || ''

export const getEmployeeHighlights = (employee: Employee, externalTalentRank?: string): string[] => {
  const highlights: string[] = []

  const addHighlight = (highlight?: string) => {
    if (!highlight || highlights.includes(highlight)) return
    highlights.push(highlight)
  }

  employee.alerts?.forEach(alert => {
    const normalizedType = normalize(alert.type)

    if (['rockstar', 'tenx', 'toptalent'].includes(normalizedType)) {
      addHighlight(EMPLOYEE_HIGHLIGHT_ROCKSTAR)
    } else if (['ninex', 'growthtalent'].includes(normalizedType)) {
      addHighlight(EMPLOYEE_HIGHLIGHT_GROWTH_TALENT)
    }
  })

  interface LastReviewSnapshot {
    selectedTalentRank?: string
    talentRank?: string
    review?: { talentRank?: string }
  }
  const lastReview = ((employee as unknown as Record<string, unknown>)?.lastReview ?? {}) as LastReviewSnapshot
  const selectedTalentRank: string | undefined =
    lastReview?.selectedTalentRank || lastReview?.talentRank || lastReview?.review?.talentRank

  // Use external talent rank from /employees/top-talent endpoint as fallback
  const effectiveTalentRank = selectedTalentRank || externalTalentRank
  const normalizedSelectedRank = effectiveTalentRank?.toLowerCase()

  if (normalizedSelectedRank) {
    if (normalizedSelectedRank.includes('10x') || normalizedSelectedRank.includes('top talent')) {
      addHighlight(EMPLOYEE_HIGHLIGHT_ROCKSTAR)
    }
    if (normalizedSelectedRank.includes('9x') || normalizedSelectedRank.includes('growth talent')) {
      addHighlight(EMPLOYEE_HIGHLIGHT_GROWTH_TALENT)
    }
  }

  return highlights
}

const mapEmployeeResponseRecord = (
  employee: EmployeeSummaryResponse,
  currentPeriod: Period,
  currentUser?: Employee
) => {
  const status = getEmployeeStatus(employee as Employee, currentUser)
  let weight

  switch (status) {
    case EMPLOYEE_STATUS_TODO:
      weight = 0
      break
    case EMPLOYEE_STATUS_UNFINISHED:
      weight = 1
      break
    case EMPLOYEE_STATUS_DONE:
    default:
      weight = 2
      break
  }

  return {
    ...employee,
    status,
    weight,
    highlights: getEmployeeHighlights(employee as Employee),
    leadersAsString: getEmployeeLeadersAsString(employee as Employee),
  }
}

const getEmployeeLeadersAsString = (employee: Employee): string => {
  if (!employee.teams) return ''
  const leaders = new Set<string>()
  employee.teams.forEach(team => {
    team.leaders?.forEach(leader => {
      if (leader?.name) leaders.add(leader.name)
    })
  })
  return Array.from(leaders).join(', ')
}

const sortByAlertDate = (employee1: Employee, employee2: Employee): number => {
  const alert1 = employee1.alerts?.[0]
  const alert2 = employee2.alerts?.[0]
  const date1 = alert1 ? new Date(alert1.lastUpdate || '') : null
  const date2 = alert2 ? new Date(alert2.lastUpdate || '') : null
  return date1 && date2 ? date1.getTime() - date2.getTime() : 0
}

export interface EmployeeMetaSearchRequest {
  technologies: Record<string, string>
  scores: Record<string, string>
  jobTitles: string[]
}

export interface EmployeeMetaSearchResponse {
  id: number
  name: string
  jobTitle: string
  teams: TeamInfoResponse[]
  leaders: EmployeeInfoResponse[]
  match: number
}

export class EmployeeService {
  static async fetchTalentRankMap(employees: EmployeeSummaryResponse[]): Promise<Map<number, string>> {
    const talentRankMap = new Map<number, string>()
    try {
      // Fetch full review details (with answers) for each employee that has a finished lastReview
      const reviewFetches = employees
        .filter(emp => emp.lastReview?.id && emp.lastReview?.finishDate)
        .map(async emp => {
          try {
            const review = await ReviewService.findOne(emp.lastReview!.id)
            const talentRankAnswer = review.answers.find(
              answer =>
                isTalentRankTitle(answer.title) ||
                isTalentRankTitle((answer as Record<string, unknown>).label as string)
            )
            if (!talentRankAnswer) return

            // Get the effective answer: use the actual answer, fall back to suggestedAnswer
            const answerValue =
              typeof talentRankAnswer.answer === 'string' && talentRankAnswer.answer
                ? talentRankAnswer.answer
                : typeof talentRankAnswer.suggestedAnswer === 'string' && talentRankAnswer.suggestedAnswer
                ? talentRankAnswer.suggestedAnswer
                : undefined
            if (answerValue) {
              talentRankMap.set(emp.id, answerValue)
            }
          } catch {
            // Skip individual review fetch failures
          }
        })

      await Promise.all(reviewFetches)
    } catch {
      // Silently fail if reviews cannot be fetched
    }
    return talentRankMap
  }

  static async findAllWithAccess(
    depth?: number,
    activePeriod?: Period,
    query?: string,
    tenX?: boolean,
    nineX?: boolean,
    currentUser?: Employee,
    signal?: AbortSignal
  ): Promise<Employee[]> {
    const currentPeriod = activePeriod || (await PeriodService.findActive())
    let url = `${basePath}/employees?depth=${depth ?? 2147483647}`
    if (query) url += `&query=${encodeURIComponent(query)}`
    if (tenX !== undefined) url += `&tenX=${tenX}`
    if (nineX !== undefined) url += `&nineX=${nineX}`
    if (currentPeriod?.startDate)
      url += `&periodStartDate=${encodeURIComponent(new Date(currentPeriod.startDate).toISOString().replace('Z', ''))}`

    return (employeeAPI as any).axios.get(url, { signal }).then((response: any) => {
      const mappedResult = response.data.map((x: any) =>
        mapEmployeeResponseRecord(x, currentPeriod, currentUser)
      ) as Employee[]
      return mappedResult
    })
  }

  static async findAll(): Promise<Employee[]> {
    return employeeAPI.findAllUsingGET().then(response => {
      return response.data as Employee[]
    })
  }

  static async findAllAsAdmin(
    page?: number,
    size?: number,
    sortDirection?: string,
    sortField?: string,
    activePeriod?: Period
  ): Promise<Employee[]> {
    const currentPeriod = activePeriod || (await PeriodService.findActive())
    const response = await employeeAPI.findPageAsAdminUsingGET(page, size, sortDirection, sortField)
    return response.data.content?.map(x => mapEmployeeResponseRecord(x, currentPeriod)) as Employee[]
  }
  static async search(query: string, signal?: AbortSignal): Promise<EmployeeInfoResponse[]> {
    const url = `${basePath}/employees/search?query=${encodeURIComponent(query)}`
    return (employeeAPI as any).axios.get(url, { signal }).then((response: any) => response.data)
  }
  static async findDirectUnder(employeeId: number, currentUser?: Employee, activePeriod?: Period): Promise<Employee[]> {
    const currentPeriod = activePeriod || (await PeriodService.findActive())
    let directUrl = `${basePath}/employees/direct/${employeeId}?depth=0`
    if (currentPeriod?.startDate)
      directUrl += `&periodStartDate=${encodeURIComponent(
        new Date(currentPeriod.startDate).toISOString().replace('Z', '')
      )}`
    return (employeeAPI as any).axios.get(directUrl).then((response: any) => {
      return response.data.map((employee: any) => {
        const status = getEmployeeStatus(employee as Employee, currentUser)
        let weight

        switch (status) {
          case EMPLOYEE_STATUS_TODO:
            weight = 0
            break
          case EMPLOYEE_STATUS_UNFINISHED:
            weight = 1
            break
          case EMPLOYEE_STATUS_DONE:
          default:
            weight = 2
            break
        }

        return {
          ...employee,
          status,
          weight,
          highlights: getEmployeeHighlights(employee as Employee),
        }
      }) as Employee[]
    })
  }

  static async findOne(employeeId: number): Promise<EmployeeDetails> {
    return employeeAPI.getByIdUsingGET(employeeId).then(response => {
      return {
        ...(response.data as EmployeeDetails),
        highlights: getEmployeeHighlights(response.data as Employee),
      }
    })
  }

  static async update(employeeId: number, employee: UpdateEmployeeRequest): Promise<void> {
    return employeeAPI.setEmployeeTeamsUsingPATCH(employeeId, employee).then()
  }

  static async create(employee: CreateEmployeeRequest): Promise<EmployeeWithTeamsAndReviewsResponse> {
    return new Promise((resolve, reject) => {
      employeeAPI
        .createUsingPOST1(employee)
        .then(r => {
          resolve(r.data)
        })
        .catch(reason => {
          reject(reason)
        })
    })
  }

  static async delete(employeeId: number): Promise<void> {
    return employeeAPI.deleteUsingDELETE(employeeId).then()
  }

  static sortEmployees<T>(employees: T[], sorting: EmployeeSort[]): T[] {
    const result = cloneDeep<T>(employees)
    return result.sort((employee1, employee2) => {
      for (const sort of sorting) {
        if (sort.property === 'alerts') {
          const alertSortResult = sortByAlertDate(employee1 as unknown as Employee, employee2 as unknown as Employee)
          if (alertSortResult !== 0) {
            return sort.order === 'asc' ? alertSortResult : alertSortResult * -1
          }
        } else {
          const sortFn = EmployeeSortFn[sort.property]
          const sortResult = sortFn?.(employee1, employee2) || 0
          if (sortResult !== 0) {
            return sort.order === 'asc' ? sortResult : sortResult * -1
          }
        }
      }
      // if there is no sort param, return a default value
      return 0
    }) as T[]
  }

  static async getSimpleReviewsForEmployeeId(id: number): Promise<ReviewSimple[]> {
    return new Promise((resolve, reject) => {
      employeeAPI
        .getReviewsSimpleByEmployeeUsingGET(id)
        .then(r => {
          resolve([...r.data] as ReviewSimple[])
        })
        .catch(reason => {
          reject(reason)
        })
    })
  }

  static getEmployeeActivity = async (employeeId: number, after?: Moment) => {
    const afterAsString = after?.format('YYYY-MM-DDT00:00:00')
    const r = await employeeAPI.getActivityUsingGET(moment().format('YYYY-MM-DDT23:59:59'), employeeId, afterAsString)
    return r.data
  }

  static getEmployeeActivityCalendar = async (employeeId: number) => {
    const r = await employeeAPI.getActivityCalendarUsingGET(employeeId)
    return r.data
  }

  static getEmployeeReviewerStatus = (reviewer: EmployeeReviewer): string => {
    if (reviewer.lastReview) {
      return reviewer.lastReview.finishDate ? EMPLOYEE_STATUS_DONE : EMPLOYEE_STATUS_UNFINISHED
    }
    return EMPLOYEE_STATUS_TODO
  }

  static setReviewersStatus(reviewers: EmployeeReviewer[]) {
    reviewers.forEach(reviewer => (reviewer.status = EmployeeService.getEmployeeReviewerStatus(reviewer)))
  }

  static async createSuggestion(messageRequest: SuggestionBoxMessageRequest) {
    return suggestionAPI.createUsingPOST1(messageRequest)
  }

  static async getAIUserProfile(employeeId: number): Promise<string> {
    return employeeAPI.getAIUserProfileUsingGET(employeeId).then(response => {
      return response.data as string
    })
  }

  static async getAllTopTalentDevelopers(tenX?: boolean, nineX?: boolean): Promise<TalentRankedDeveloper[]> {
    const params = new URLSearchParams()
    if (tenX !== undefined) params.append('tenX', String(tenX))
    if (nineX !== undefined) params.append('nineX', String(nineX))

    const url = `${basePath}/employees/top-talent/all${params.toString() ? `?${params.toString()}` : ''}`
    return (employeeAPI as any).axios.get(url).then((response: any) => {
      return (response.data as TalentRankedDeveloper[]).map(developer => ({
        ...developer,
        leaders: developer.leaders ?? [],
      }))
    })
  }

  static async getJobTitles(): Promise<string[]> {
    return (employeeAPI as any).axios.get(`${basePath}/employees/jobTitles`).then((response: any) => {
      return response.data as string[]
    })
  }

  static async getFilteredEmployees(
    metaSearchRequest: EmployeeMetaSearchRequest
  ): Promise<EmployeeMetaSearchResponse[]> {
    return (employeeAPI as any).axios
      .post(`${basePath}/employees/metaSearch`, metaSearchRequest)
      .then((response: any) => {
        return response.data as EmployeeMetaSearchResponse[]
      })
  }
}
